/* import __COLOCATED_TEMPLATE__ from './fin-custom.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type FinCustom from 'embercom/models/operator/visual-builder/step/fin-custom';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type FinCustomConfiguration from 'embercom/objects/visual-builder/configuration/step/fin-custom';
import type InsertableAction from 'embercom/models/workflow-connector/insertable-action';
import { action } from '@ember/object';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { Type } from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import { type ComponentLike } from '@glint/template';
import type { TaskGenerator, Yieldable } from 'ember-concurrency';
import type AttributeDescriptorListOption from 'embercom/models/operator/visual-builder/attribute-descriptor-list-option';
import generateUUID from 'embercom/lib/uuid-generator';
import type IntlService from 'ember-intl/services/intl';
import type LogService from 'embercom/services/log-service';
import { type Block } from 'embercom/models/common/blocks/block';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import CommandsResolver from 'embercom/lib/workflows/commands-resolver';
import { post } from 'embercom/lib/ajax';
import { showFinTaskSimpleMode } from 'embercom/lib/workflows/helpers/fin-task';
import { EntityType } from 'embercom/models/data/entity-types';
import { APP_ATTRIBUTES } from 'embercom/lib/common/template-attribute-resolver';
import type Admin from 'embercom/models/admin';
import type Tag from 'embercom/models/tag';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
import { camelize } from '@ember/string';

export function camelizeKeys(hash: object): object | null {
  if (!hash) {
    return null;
  }

  let entries = Object.entries(hash);
  let camelizedEntries = entries.map(([key, value]) => {
    let camelizedValue = value;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      camelizedValue = camelizeKeys(value);
    }
    return [camelize(key), camelizedValue];
  });
  return Object.fromEntries(camelizedEntries);
}

export interface ListOption {
  id: string;
  value: string;
}

export interface OutputParam {
  id?: string;
  name: string;
  description: string;
  descriptorType: Type;
  listOptions: ListOption[];

  attributeType?: string;

  // For conversation attributes
  multiline?: boolean;
}

// Attribute and AttributeCollection types are, upsettingly, also defined in:
// apps/teammate-app/src/features/fin-testing-suite/utils/attributes.ts
export interface Attribute {
  identifier: string;
  name: string;
  type: string;
  description: string;
  category: 'user' | 'conversation' | 'temporary' | 'company';
}

export interface AttributeCollection {
  userAttributes: Attribute[];
  companyAttributes: Attribute[];
  conversationAttributes: Attribute[];
  temporaryAttributes: Attribute[];
  referencedAttributeIdentifiers: Set<string>;
}

export interface EscalationDestination {
  id: string;
  name: string;
  avatar?: {
    color: string;
    imageUrls: {
      square25: string;
      square50: string;
      square128: string;
    };
    initials: string;
  };
}

export interface EscalationDestinationsGroup {
  heading?: string;
  attributes: EscalationDestination[];
}

export interface EscalationDestinationsCollection {
  defaultTeam: EscalationDestinationsGroup;
  teams: EscalationDestinationsGroup;
  teammates: EscalationDestinationsGroup;
}

export interface FinTasksEditorPayload {
  workflowId: string;
  appId: string;
  allowedAttributes: AttributeCollection;
  escalationDestinations: EscalationDestinationsCollection;
  step: Partial<FinCustom>;
  outputParams: OutputParam[];
  readOnly: boolean;
  attributesConfig: $TSFixMe;
  commandsConfig: {
    actions: any[];
    tags: any[];
    teammates: any[];
  };
}

interface Arguments {
  step: FinCustom;
  stepConfig: FinCustomConfiguration;
  editorState: EditorState;
  readOnly: boolean;
  isTemplatePreview: boolean;
  connectionPointComponent: ComponentLike<{ connectionPoint: ConnectionPoint }>;
  updateConnectionPoints: (node: Node) => Generator<Yieldable<void>, void, unknown>;
}

function referencedAttributeTemplatableFields(step: FinCustom): Array<string> {
  let instructions = step.instructions || [];

  return instructions.flatMap((instruction: Block) => {
    return extractTemplateVariables(instruction, true);
  });
}

export function allowedAttributesForTestConfigPanel(
  attributeService: $TSFixMe,
  step: FinCustom,
): AttributeCollection {
  let userAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    displayable: boolean;
    type: string;
    templatable_identifier: string;
  }> = attributeService.userAttributes;

  let companyAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    displayable: boolean;
    type: string;
    templatable_identifier: string;
  }> = attributeService.companyAttributes.map((attr: any) => attr.attribute); // don't know why this extra layer exists for company attrs

  let conversationAttributes: Array<{
    identifier: string;
    description: string;
    name: string;
    type: string;
    templatable_identifier: undefined; // doesn't exist here
  }> = attributeService.conversationAttributes;

  let temporaryAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    type: string;
    templatable_identifier: string;
  }> = step.editorConfig?.localAttributes ?? [];

  return {
    userAttributes: userAttributes
      .filter((attr) => attr.displayable)
      .map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description,
        type: attr.type,
        category: 'user',
      })),
    companyAttributes: companyAttributes
      .filter((attr) => attr.displayable)
      .map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description,
        type: attr.type,
        category: 'company',
      })),
    conversationAttributes: conversationAttributes.map((attr) => ({
      identifier: attr.identifier,
      name: attr.name,
      description: attr.description,
      type: attr.type,
      category: 'conversation',
    })),
    temporaryAttributes: temporaryAttributes.map((attr) => ({
      identifier: attr.identifier,
      name: attr.name,
      description: attr.description || '',
      type: attr.type,
      category: 'temporary',
    })),
    referencedAttributeIdentifiers: ((): Set<string> => {
      let allAttributes = [
        ...userAttributes,
        ...companyAttributes,
        ...conversationAttributes,
        ...temporaryAttributes,
      ];
      let attributeIdentifiersByTemplatableFields: Record<string, string> = {};

      allAttributes.forEach((attr) => {
        // if templatable_identifier is not defined, the identifer is equal to templatable_identifier
        let templatableField = attr.templatable_identifier ?? attr.identifier;
        attributeIdentifiersByTemplatableFields[templatableField] = attr.identifier;
      });
      let referenceIdentifiers = referencedAttributeTemplatableFields(step).map((field) => {
        return attributeIdentifiersByTemplatableFields[field];
      });
      // filter out any that could not be looked up
      return new Set(referenceIdentifiers.filter((a) => a != null));
    })(),
  };
}

function mentionedEscalationDestinations(step: FinCustom): Set<string> {
  let mentioned = new Set<string>();
  let instructions = step.instructions || [];

  // Extract template variables from all instruction blocks
  let templateVariables = instructions.flatMap((instruction) => {
    return extractTemplateVariables(instruction, true);
  });

  // Filter for assignee variables (escalation destinations)
  templateVariables.forEach((variable) => {
    if (variable.startsWith('assignee.')) {
      mentioned.add(variable);
    }
  });

  return mentioned;
}

export function escalationDestinationsForTestConfigPanel(
  stepConfig: FinCustomConfiguration,
  step: FinCustom,
) {
  let resolver = stepConfig.commandsResolver;
  let mentionedDestinations = mentionedEscalationDestinations(step);
  // Filter helper - always include default, plus any mentioned destinations
  let filterDestinations = (destinations: any[], isDefaultTeam = false) => {
    let filtered = destinations.filter((attr: any) => {
      if (isDefaultTeam) {
        return true;
      }

      return mentionedDestinations.size !== 0 && mentionedDestinations.has(attr.identifier);
    });

    return filtered.map((attr: any) => ({
      id: attr.identifier.split('.')[1] ?? attr.identifier,
      name: attr.name,
      avatar: attr.object?.avatar ? camelizeKeys(attr.object.avatar) : undefined,
    }));
  };

  return {
    defaultTeam: {
      ...resolver.defaultTeam,
      attributes: filterDestinations(resolver.defaultTeam.attributes, true),
    },
    teams: {
      ...resolver.teams,
      attributes: filterDestinations(resolver.teams.attributes, false),
    },
    teammates: {
      ...resolver.teammates,
      attributes: filterDestinations(resolver.teammates.attributes, false),
    },
  };
}

export default class FinCustomNodeStep extends Component<Arguments> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare modalService: $TSFixMe;
  @service declare attributeService: $TSFixMe;
  @service declare logService: LogService;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);

    if (this.args.step.outwardConnectionPoints.length === 0 && !this.args.step.isAgentic) {
      this.args.step.outwardConnectionPoints.addObject(
        this.store.createRecord('operator/visual-builder/connection-point', { type: 'fallback' }),
      );
    }
  }

  get app() {
    return this.appService.app;
  }

  get allowedAttributes(): AttributeCollection {
    return allowedAttributesForTestConfigPanel(this.attributeService, this.args.step);
  }

  get escalationDestinations() {
    return escalationDestinationsForTestConfigPanel(this.args.stepConfig, this.args.step);
  }

  get stepName() {
    return this.args.step.name;
  }

  get validationErrors() {
    return this.args.step.validations.errors;
  }

  get firstGoalBlock() {
    let blocks = this.args.step.blocks?.serialize() ?? [];
    blocks = blocks.filter((block: Block) => !!block.text);

    if (blocks.length > 0) {
      return blocks[0];
    }
  }

  get shouldShowError() {
    return this.args.editorState.shouldShowValidations && !this.args.step.validations.isValid;
  }

  get shouldShowOutputError() {
    return (
      this.args.editorState.shouldShowValidations &&
      !this.args.step.validations.attrs.outwardConnectionPoints.isValid
    );
  }

  get shouldShowFallbackBranch() {
    return !this.args.step.isAgentic;
  }

  get fallbackBranch(): ConnectionPoint {
    return this.args.step.outwardConnectionPoints.firstObject!;
  }

  get outputParams() {
    return this.args.step.finOutputParams.map((descriptor: AttributeDescriptor) => {
      return {
        name: descriptor.name,
        description: descriptor.description,
        id: descriptor.id,
        descriptorType: descriptor.type,
        listOptions: descriptor.listOptions
          .toArray()
          .map((option: AttributeDescriptorListOption) => {
            return {
              id: option.id,
              value: option.label,
            };
          }) as ListOption[],
      } as OutputParam;
    });
  }

  get showFinTaskSimpleMode() {
    return showFinTaskSimpleMode(
      this.appService.app,
      this.args.editorState,
      this.args.isTemplatePreview,
    );
  }

  @action
  async addOutputParam(param: OutputParam) {
    let attributeDescriptor: any;

    switch (param.attributeType) {
      case 'conversation':
        attributeDescriptor = await this.createConversationAttribute(param);
        break;
      default:
        attributeDescriptor = this.createOrUpdateTemporaryAttribute(param);
    }

    return attributeDescriptor;
  }

  buildFinTasksEditorPayload(): FinTasksEditorPayload {
    let payload = {
      workflowId: this.args.editorState.workflow.workflowInstanceEntityId,
      allowedAttributes: this.allowedAttributes,
      escalationDestinations: this.escalationDestinations,
      step: {
        id: this.args.step.id,
        externalStepId: this.args.step.externalStepId,
        instructions:
          this.args.step.finProcedureContent?.content?.length > 0
            ? this.args.step.finProcedureContent.content
            : this.args.step.instructions,
        guideline: this.args.step.guideline.map((block: Block) => block.serialize()),
      },
      attributesConfig: this.buildAttributesForFinTasksEditor(),
      commandsConfig: this.buildCommandsConfig(),
      readOnly: this.args.readOnly,
      appId: this.appService.app.id,
      workflowInstanceId: this.args.editorState.workflow.workflowInstanceId,
      externalStepId: this.args.step.externalStepId,
    };

    // Force JSON serialization/deserialization to ensure it's strictly JSON
    return JSON.parse(JSON.stringify(payload));
  }

  buildAttributesForFinTasksEditor() {
    let attributeInfoResolver = this.createAttributeInfoResolver();
    let updateableAttributeInfoResolver = this.args.stepConfig.attributeInfoResolveForUpdates;
    let customAttributes = this.buildCustomAttributes(attributeInfoResolver);

    return {
      attributes:
        this.args.step.editorConfig?.runtimeMatchingTargetingAttributes
          ?.filter((group: any) => group.heading !== 'Temporary attributes')
          ?.map(this.convertAttributeGroupForReact) || [],
      updateableAttributes: (updateableAttributeInfoResolver?.attributes || []).map(
        this.convertAttributeToPlainObject,
      ),
      includeAppAttributes: true,
      includeCustomObjectAttributes: true,
      includeFileAttributes: this.args.step.appService.app.canUseFinCustomImageRecognition,
      manualAppAttributes: APP_ATTRIBUTES,
      showCategoryPrefixes: false,
      customActions: this.args.stepConfig.finCustomActionsGroupList(),
      localAttributes: this.getLocalAttributesExcludingArchived(),
      allLocalAttributes: this.getLocalAttributes(),
      ...customAttributes,
      workflowInstanceId: this.args.editorState.workflow.workflowInstanceId,
    };
  }

  private createAttributeInfoResolver() {
    return new AttributeInfoResolver({
      attributes:
        this.args.step.editorConfig?.runtimeMatchingTargetingAttributes
          .map((group) => group.attributes)
          .flat() ?? this.attributeService.userEditorMetadataAndConversationAttributes,
      customObjectAttributes: this.attributeService.customObjectsAttributeDescriptors,
      includeAppAttributes: true,
      includeCustomObjectAttributes: true,
      includeFileAttributes: this.args.step.appService.app.canUseFinCustomImageRecognition,
      localAttributes: this.getLocalAttributesExcludingArchived(),
      allLocalAttributes: this.getLocalAttributes(),
      customActions: this.args.stepConfig.finCustomActionsGroupList(),
      showCategoryPrefixes: false,
    });
  }

  private getLocalAttributes() {
    return (
      this.args.editorState.workflow.attributeDescriptors
        .toArray()
        .map(this.convertAttributeWithIdentifiers) || []
    );
  }

  private getLocalAttributesExcludingArchived() {
    return (
      this.args.editorState.workflow.attributeDescriptors
        .filter((attr: any) => !attr.archived)
        .toArray()
        .map(this.convertAttributeWithIdentifiers) || []
    );
  }

  private convertAttributeWithIdentifiers = (attr: any) => {
    // Convert to plain object to avoid circular references
    let plainAttr = this.convertAttributeToPlainObject(attr);
    let isWorkflowLocalVariable = attr.templatable_identifier?.startsWith(
      'workflow_local_variables.',
    );
    return {
      ...plainAttr,
      identifier: attr.identifier,
      templatable_identifier: attr.templatable_identifier,
      isTemporary: isWorkflowLocalVariable,
    };
  };

  private convertAttributeGroupForReact = (group: any) => {
    return {
      heading: group.heading,
      attributes: (group.attributes || []).map(this.convertAttributeToPlainObject),
    };
  };

  private convertAttributeToPlainObject = (attr: any) => {
    let plainAttr;
    if (attr.serialize) {
      plainAttr = attr.serialize();
    } else {
      plainAttr = attr;
    }

    return {
      ...plainAttr,
      isUser: attr.isUser,
      isCompany: attr.isCompany,
      isConversation: attr.isConversation,
      isTemporary: attr.isTemporary,
      isApp: attr.isApp,
      isUserEvent: attr.isUserEvent,
      referenceOnly: attr.referenceOnly,
    };
  };

  private buildCustomAttributes(attributeInfoResolver: AttributeInfoResolver) {
    let customAttributes = {
      customConversationAttributes: [],
      ticketAttributes: [],
      ticketTypeAttributes: [],
    };

    if (
      !attributeInfoResolver.includeCustomObjectAttributes &&
      !attributeInfoResolver.includeCustomActionAttributes
    ) {
      return customAttributes;
    }

    if (attributeInfoResolver.includeCustomObjectAttributes) {
      let conversationAttributes = attributeInfoResolver.includeFileAttributes
        ? this.attributeService.nonArchivedConversationCustomAttributesWithFileS3Key
        : this.attributeService.nonArchivedConversationCustomAttributesWithoutFile;

      customAttributes.customConversationAttributes = conversationAttributes.map(
        this.convertAttributeToPlainObject,
      );
    }

    if (attributeInfoResolver.includeCustomActionAttributes) {
      let conversationAttributes = attributeInfoResolver.includeFileAttributes
        ? this.attributeService.conversationAttributesForCustomActionWithPresignedUrl
        : this.attributeService.conversationAttributesForCustomAction;

      customAttributes.customConversationAttributes = conversationAttributes.map(
        this.convertAttributeToPlainObject,
      );
      customAttributes.ticketAttributes = this.attributeService.ticketAttributesForCustomAction.map(
        this.convertAttributeToPlainObject,
      );
      customAttributes.ticketTypeAttributes = this.attributeService.ticketAttributesByType.map(
        (ticketTypeAttribute: any) => ({
          category: ticketTypeAttribute.category,
          heading: ticketTypeAttribute.heading,
          attributes: ticketTypeAttribute.attributes.map(this.convertAttributeToPlainObject),
        }),
      );
    }

    return customAttributes;
  }

  buildCommandsConfig() {
    return {
      actions: this.getCustomActions(),
      tags: this.getTags(),
      teammates: this.getTeammates(),
      teams: this.getTeams(),
    };
  }

  getCustomActions() {
    let customActions = this.store
      .peekAll('workflow-connector/insertable-action')
      .filter((action: InsertableAction) => action.state === 'live' && !action.isTemplate)
      .filter(
        (action: InsertableAction) =>
          action.usage === 'fin' || this.appService.app.canUseConsolidatedActions,
      )
      .map((action: InsertableAction) => ({
        identifier: `workflow_connector.${action.id}`,
        templatable_identifier: `workflow_connector.${action.id}`,
        name: action.name,
        composer_name: this.args.step.isV3
          ? action.name
          : this.intl.t(
              'operator.workflows.visual-builder.fin-custom-settings.configure.prompt.custom-action',
              { action_name: action.name },
            ),
        type: 'string',
        icon: 'webhook',
        noFallback: true,
      }));

    return customActions;
  }

  getTags() {
    let tags = this.store.peekAll('tag').map((tag: Tag) => ({
      identifier: `tag.${tag.id}`,
      templatable_identifier: `tag.${tag.id}`,
      name: tag.name,
      composer_name: tag.name,
    }));

    return tags;
  }

  getTeammates() {
    let teammates = (this.appService.app.assignableHumanAdmins || []).map((teammate: Admin) => ({
      identifier: `assignee.${teammate.id}`,
      templatable_identifier: `assignee.${teammate.id}`,
      name: teammate.name,
      composer_name: teammate.name,
    }));

    return teammates;
  }

  getTeams() {
    return this.appService.app.assignableTeams.map((team: any) => ({
      identifier: `assignee.${team.id}`,
      templatable_identifier: `assignee.${team.id}`,
      name: team.name,
      composer_name: team.name,
    }));
  }

  get commandsResolver() {
    return new CommandsResolver(this.buildCommandsConfig());
  }

  @action
  openFinTasksEditorSheet() {
    this.modalService.openModal('react/workflows/fin-tasks-editor-sheet', {
      open: true,
      finTasksEditorPayload: this.buildFinTasksEditorPayload(),
      onClose: (data: FinTasksEditorPayload) => {
        if (this.app.canUseFinTasksDebugNullValueLogs) {
          console.log('data arriving in ember', data); // eslint-disable-line no-console
        }
        if (data?.step) {
          this.updateStepFromReactData(data.step);
        }
        this.modalService.closeModal();
      },
    });
  }

  @action
  updateStepFromReactData(stepData: any) {
    if (this.app.canUseFinTasksDebugNullValueLogs) {
      console.log('stepData', stepData); // eslint-disable-line no-console
    }
    try {
      let updatableProperties = ['name'] as const;
      let updatedProperties: string[] = [];

      updatableProperties.forEach((property) => {
        if (stepData[property] !== null) {
          this.args.step.set(property, stepData[property]);
          updatedProperties.push(property);
        }
      });

      if (stepData.instructions !== null) {
        this.args.step.finProcedureContent.content = stepData.instructions;
        updatedProperties.push('finProcedureContent');
      }

      if (stepData.guideline !== null) {
        let guidelineBlocks = stepData.guideline.map((block: Block) =>
          createFragmentFromBlock(this.store, block),
        );
        this.args.step.set('guideline', guidelineBlocks);
        updatedProperties.push('guideline');
      }

      if (stepData.newLocalAttributes !== null) {
        stepData.newLocalAttributes.forEach((attrData: any) => {
          let testParam: OutputParam = {
            id: attrData.id,
            name: attrData.name,
            description: attrData.description,
            descriptorType: attrData.type,
            listOptions: attrData.listOptions || [],
          };

          this._createTemporaryAttribute(testParam);
        });
      }

      updatedProperties.forEach((property) => {
        this.args.step.notifyPropertyChange(property);
      });

      // Special handling for fragment content notification
      if (updatedProperties.includes('finProcedureContent')) {
        this.args.step.finProcedureContent.notifyPropertyChange('content');
      }

      /* eslint-disable no-console */
      if (this.app.canUseFinTasksDebugNullValueLogs) {
        console.log('updateStepFromReactDataSuccess', {
          originalStepData: stepData,
          finalEmberState: {
            stepName: this.args.step.name,
            finProcedureContent: this.args.step.finProcedureContent?.content,
            guidelineLength: this.args.step.guideline?.length,
            finOutputParamsCount: this.args.step.finOutputParams?.length,
          },
        });
      }
      /* eslint-disable no-console */
    } catch (error) {
      this.logService.log({
        log_type: 'updateStepFromReactDataError',
        stepData,
        error,
      });
    }
  }

  @action
  updateOutputParam(param: OutputParam) {
    this.args.step.finOutputParams = this.args.step.finOutputParams.map((descriptor) => {
      if (descriptor.id !== param.id) {
        return descriptor;
      }

      this.updateDescriptorProperties(descriptor, param);

      descriptor.listOptions = this.buildUpdatedListOptions(descriptor, param.listOptions);
      return descriptor;
    }) as $TSFixMe;
  }

  @action
  copyActionOutputParam(action: InsertableAction) {
    let identifier = this.args.step.actionOutputAttributeDescriptorIdentifier;

    if (identifier) {
      let existingAttributeDescriptor = this.store.peekRecord(
        'operator/visual-builder/attribute-descriptor',
        identifier,
      );
      this.args.editorState.deleteLocalVariable(existingAttributeDescriptor);
    }

    if (
      action.actionOutputParameterDescriptor &&
      action.actionOutputParameterDescriptor.sourcePath &&
      action.actionOutputParameterDescriptor.type
    ) {
      let sourceObject = {
        type: EntityType.WorkflowConnectorAction,
        id: action.id,
        path: action.actionOutputParameterDescriptor.sourcePath,
      };

      let newAttributeDescriptor = this.args.editorState.copyActionLocalVariable(
        action.actionOutputParameterDescriptor,
        action.name,
        sourceObject,
      );

      if (newAttributeDescriptor) {
        this.args.step.actionOutputAttributeDescriptorIdentifier =
          newAttributeDescriptor.identifier;
      }
    }
  }

  private updateDescriptorProperties(descriptor: AttributeDescriptor, param: OutputParam) {
    if (descriptor.name !== param.name) {
      descriptor.name = param.name;
    }
    if (descriptor.description !== param.description) {
      descriptor.description = param.description;
    }
    if (descriptor.type !== param.descriptorType) {
      descriptor.type = param.descriptorType;
    }
  }

  private buildUpdatedListOptions(descriptor: AttributeDescriptor, newOptions: ListOption[]) {
    return newOptions.map((option) => {
      if (option.id) {
        let existingOption = descriptor.listOptions.findBy('id', option.id);
        if (existingOption) {
          if (existingOption.label !== option.value) {
            existingOption.label = option.value;
          }
          return existingOption;
        }
      }

      return this.store.createRecord('operator/visual-builder/attribute-descriptor-list-option', {
        id: generateUUID(),
        label: option.value,
        descriptor,
      });
    });
  }

  private async createConversationAttribute(param: OutputParam) {
    let attributeDescriptor = this.store.createRecord('conversation-attributes/descriptor', {
      name: param.name,
      description: param.description,
      dataType: param.descriptorType,
      multiline: param.multiline,
    });

    if (param.descriptorType === 'list') {
      param.listOptions.forEach((option) => {
        attributeDescriptor.listOptions.createFragment({
          listOptionId: generateUUID(),
          label: option.value,
        });
      });
    }

    await attributeDescriptor.save();
    return attributeDescriptor;
  }

  private createOrUpdateTemporaryAttribute(param: OutputParam) {
    if (param.id) {
      return this._updateTemporaryAttribute(param);
    } else {
      return this._createTemporaryAttribute(param);
    }
  }

  private _createTemporaryAttribute(param: OutputParam) {
    let attributeDescriptor = this.args.editorState.createLocalVariable(
      param.name,
      param.descriptorType ?? Type.string,
      false,
      undefined,
      param.id,
    );
    attributeDescriptor.description = param.description;

    if (
      attributeDescriptor.type === 'list' ||
      attributeDescriptor.type === 'workflow_attribute_descriptor_list'
    ) {
      this._updateTemporaryAttributeListOptions(param, attributeDescriptor, true);
    }

    this.args.step.finOutputParams.addObject(attributeDescriptor);

    return attributeDescriptor;
  }

  private _updateTemporaryAttribute(param: OutputParam) {
    let existingAttrDescriptor = this.args.step.finOutputParams.findBy('id', param.id);
    if (!existingAttrDescriptor) {
      return;
    }
    existingAttrDescriptor.name = param.name;
    existingAttrDescriptor.type = param.descriptorType;
    existingAttrDescriptor.description = param.description;

    if (
      existingAttrDescriptor.type === 'list' ||
      existingAttrDescriptor.type === 'workflow_attribute_descriptor_list'
    ) {
      this._updateTemporaryAttributeListOptions(param, existingAttrDescriptor, false);
    }

    return existingAttrDescriptor;
  }

  private _updateTemporaryAttributeListOptions(
    param: OutputParam,
    attributeDescriptor: AttributeDescriptor,
    isNewAttribute: boolean,
  ) {
    for (let option of param.listOptions ?? []) {
      if (option.id) {
        let listOption = attributeDescriptor.listOptions.findBy('id', option.id);
        if (listOption) {
          listOption.label = option.value;
        }
      } else {
        let listOption = this.store.createRecord(
          'operator/visual-builder/attribute-descriptor-list-option',
          {
            id: generateUUID(),
            label: option.value,
            descriptor: attributeDescriptor,
          },
        );

        attributeDescriptor.listOptions.pushObject(listOption);
      }
    }

    if (!isNewAttribute) {
      attributeDescriptor.listOptions = attributeDescriptor.listOptions.filter((option) =>
        param.listOptions.some((newOption) => newOption.value === option.label),
      );
    }
  }

  @action
  removeOutputParam(outputParam: OutputParam) {
    let attributeDescriptor = this.args.step.finOutputParams.findBy('id', outputParam.id);
    if (!attributeDescriptor) {
      return;
    }

    this.args.step.finOutputParams = this.args.step.finOutputParams.filter(
      (param: AttributeDescriptor) => param.id !== outputParam.id,
    ) as $TSFixMe;

    this.args.editorState.deleteLocalVariable(attributeDescriptor);

    this.args.step.notifyPropertyChange('finOutputParams');
  }

  get isSummarizingStep() {
    return taskFor(this.performInstructionBlockSummarizationTask).isRunning;
  }

  @action
  performInstructionBlockSummarization(step: FinCustom) {
    if (this.args.readOnly || !step.hasUnsavedChanges) {
      return;
    }

    let hasEmptyInstructions = step.instructions.every((instructionBlocks) =>
      instructionBlocks.every((block: Block) => !block.hasContent),
    );

    if (hasEmptyInstructions) {
      step.name = null;
      return;
    }

    taskFor(this.performInstructionBlockSummarizationTask).perform(step);
  }

  @task({ enqueue: true })
  *performInstructionBlockSummarizationTask(step: FinCustom): TaskGenerator<void> {
    let response = yield post(`/ember/operator_workflows/summarize_fin_task_instructions`, {
      app_id: this.app.id,
      instructions: step.instructions,
      existing_summary: step.name,
    });

    if (response.outcome === 'failure' || !response.summary) {
      return;
    }

    step.name = response.summary;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom': typeof FinCustom;
    'workflows/graph-editor/node-items/steps/fin-custom': typeof FinCustom;
  }
}
